import { z } from 'zod';
import { getAdomainUrlError } from '../lib/urlValidation';

// --- Publisher Configuration ---

export interface PublisherMarket {
  dmaCode: string;
  dmaName: string;
  channels: string[];
  // CTV-421 Phase 2: the US states this market's signal reaches. Single-DMA
  // stations are typically one state; border stations declare multiple
  // (e.g., KEZI/Eugene is Oregon but bleeds into Northern California, so
  // ['OR', 'CA']). Auto-filled from the DMA name parser in PublisherForm;
  // staff can edit. Surfaced as Suggested States in the wizard's geo tabs.
  // Optional so legacy `markets` JSON rows that pre-date this field still
  // parse cleanly (treated as empty when absent).
  coveredStates?: string[];
}

export interface ProductOption {
  id: string;
  name: string;
  description: string;
  cpm: number;
  subChannels: string[];
}

// CTV-518: CTVBuyer is merchant of record. The buyer pays
// budget + tax + service fee, where the service fee grosses up Stripe
// processing on the whole charge. Rates come from the stations endpoint
// (`serviceFee: { percent, fixedCents }`) so the frontend estimator can't
// drift from the backend's env-configured rates.
export interface ServiceFeeRates {
  percent: number;
  fixedCents: number;
}

// Fallback when the API payload omits serviceFee (e.g. stale cache shape).
// Matches the backend defaults: 2.9% + 30¢.
export const DEFAULT_SERVICE_FEE_RATES: ServiceFeeRates = { percent: 0.029, fixedCents: 30 };

// CTV-558: platform-wide budget floor enforced at the wizard layer (see
// ADR CTV-558). The "Min $X" caption and Zod refinement use
// `effectiveMinBudget(config.minBudget)` so a publisher's lower `min_budget`
// is raised to this floor in production. Dev-mode bypass below.
// Temporarily relaxed to $1 so ops can push $1 test campaigns on prod without
// a per-publisher DB edit; each publisher's own `min_budget` still applies.
export const PLATFORM_MIN_BUDGET = 1;

// Returns the effective minimum the wizard enforces. Production raises any
// sub-platform-floor publisher value to PLATFORM_MIN_BUDGET; dev-mode passes
// the publisher value through (or 0 when null) so ops can push $1 test
// campaigns without DB edits.
export const effectiveMinBudget = (publisherMinBudget: number | null | undefined, isDev: boolean): number => {
  const pub = publisherMinBudget ?? 0;
  return isDev ? pub : Math.max(PLATFORM_MIN_BUDGET, pub);
};

export interface PublisherConfig {
  slug: string;
  // Raw internal name (= stations.name). What JWT app_metadata.stations[]
  // entries match against and what the admin form's "Internal name" input
  // binds to. Consumer-facing surfaces (browse cards, headers, etc.) should
  // call displayedName(config) instead to fall back through displayName.
  name: string;
  organizationId: string | null;
  // Raw display-name column (nullable). When null, displayedName() falls
  // back to `name`. Bound directly to the admin form's "Display name" input.
  displayName?: string | null;
  logoUrl: string;
  logoInvert?: boolean; // invert logo colors to render white on dark header (default true)
  // CTV-431: header-render scale multiplier (0.5–3.0, default 1.0). Lets
  // admins enlarge small wordmark/icon logos without re-uploading the asset.
  logoScale?: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  fontUrl?: string; // Google Fonts URL to load
  coverage?: 'national' | 'regional'; // default 'regional'
  markets: PublisherMarket[];
  // CTV-674: DMA codes the wizard's DMA picker may offer, computed server-
  // side as markets within 50 miles of the coverage DMAs. Null/absent means
  // no restriction (station declares no coverage DMAs, or the proximity
  // query failed open).
  allowedDmaCodes?: string[] | null;
  products: ProductOption[];
  // CTV-518: publisher-facing economics only — CTVBuyer's share of media
  // spend. Never used in buyer-facing price math (the buyer pays
  // budget + tax + service fee; rev share comes out of the publisher payout).
  revSharePercent: number;
  // Buyer-facing processing gross-up rates from the stations endpoint.
  serviceFeeRates: ServiceFeeRates;
  minBudget: number;
  minCampaignDays: number;
  // CTV-489: home-market centroid derived from markets[0]'s Nielsen DMA.
  // Drives the wizard's default radius center so the map is anchored on
  // the station before the buyer types an address.
  homeMarketCenter?: { lat: number; lng: number; label: string } | null;
  supportEmail: string;
  // CTV-680: Ad Ops / Technical Contact, entered on the publisher intake or
  // by an admin; written to stations.adops_email/adops_phone.
  adopsEmail: string;
  adopsPhone: string;
  // CTV-671 station default for notional commission visualizations
  // (whole-number percent; null = not configured).
  defaultCommissionPercent: number | null;
  /**
   * Whether the commission checkbox starts checked. UI intent only, never
   * persisted: a template carries the publisher's toggle (which may be
   * enabled with no %), an existing station derives it from the % itself.
   */
  teamCommissionEnabled?: boolean;
  termsUrl: string;
  // CTV-610: rendered landing-page TV-hero background video. Null/absent means
  // the hero falls back to the accent gradient. Set by the render job.
  backgroundVideoUrl?: string | null;
  // CTV-752: shows/hides the publisher admin's Materials tab for this
  // station. Absent means enabled (the column default).
  materialsEnabled?: boolean;
  // CTV-803: devices the station streams on (canonical values from
  // STREAMING_DEVICES in publisherIntake.ts). Absent means none declared.
  devices?: string[];
  // CTV-840: saved TV-screen stills for the display ads. Empty = plain screen.
  displayAdStills?: DisplayAdStill[];
  // CTV-863: the station's own commercials, offered in the Materials tab's
  // Videos group. Empty = no group for the station's team.
  materialsVideos?: MaterialsVideo[];
}

/** CTV-863: one commercial made for the station, downloadable as it is. */
export interface MaterialsVideo {
  id: string;
  name: string;
  /** In the uploader's words; empty when none was given. */
  description: string;
  videoUrl: string;
  storagePath: string;
  createdAt: string;
}

/** One saved frame of the station's footage, shown on the TV in a display ad. */
export interface DisplayAdStill {
  id: string;
  name: string;
  imageUrl: string;
  storagePath: string;
  sourceStoragePath: string | null;
  sourceSeconds: number | null;
  createdAt: string;
}

// --- Submission row ---
// Canonical shape of a row from `selfservice_submissions`. Each page Picks
// the subset it needs; pages that previously hand-rolled a near-duplicate
// type should import this and Pick instead.

export interface SelfServiceBusinessInfo {
  businessName?: string;
  businessWebsite?: string;
  contactName?: string;
  contactPhone?: string;
}

export interface SelfServiceCampaign {
  campaignName?: string;
  budget?: number;
  startDate?: string;
  endDate?: string;
  selectedProduct?: string;
  // CTV-759: the product's NAME, resolved server-side. Present only on
  // publisher-facing rows (list-by-publisher). The Submissions table used to
  // look the id up in `config.products` — one station's catalogue — which
  // mislabels rows once the list spans stations.
  selectedProductName?: string;
  targetMarkets?: string[];
  targetingMode?: 'default' | 'custom';
  geoTargetCenter?: { lat: number; lng: number; label: string } | null;
  geoTargetRadiusMiles?: number;
  geoTargetZips?: string[];
  clickThroughUrl?: string;
}

// Mirrors SelfserviceSubmissionStatus in selfservice-api/src/db/types.ts.
export type SelfServiceSubmissionStatus =
  | 'pending_payment'
  | 'payment_authorized'
  | 'creative_rejected'
  | 'payment_failed'
  | 'payment_refunded'
  | 'approved'
  | 'archived'
  | 'canceled';

export type SelfServicePaymentPlan = 'upfront' | 'monthly';

// CTV-462: a row in selfservice_flights identifying the Current Flight on a
// Submission for progress-bar / "where are we now" UI. See CONTEXT.md.
export interface SelfServiceCurrentFlight {
  id: string;
  installment_number: number | null;
  start_date: string;
  end_date: string;
  status: string;
  impressions: number | null;
  pacing_impressions: number | null;
}

// CTV-440: one creative row in the Live Set (status='active') for a
// submission. The submission's creatives[] is what the worker pushes to
// Publica's AdserverCreatives[]. See docs/adr/0002.
export interface SelfServiceCreativeRow {
  id: string;
  filename: string;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  name: string | null;
  // CTV-476: per-segment shares replace the legacy `weight` scalar.
  segment_shares: Array<{ start_date: string; end_date: string; share: number }>;
  click_through_url: string | null;
  // CTV-457: NOT NULL post-consolidation. Always carries an explicit window.
  creative_start_date: string;
  creative_end_date: string;
  status: 'active' | 'pending_review' | 'voided' | 'retired' | 'withdrawn' | 'abandoned';
  publica_global_mediafile_id: number | null;
  // ADR CTV-732: `publica_library_miss_at` is no longer serialized to
  // this surface. It only ever fed the publisher-facing "upload to Publica"
  // tag, and publisher review is now Publica-free. The super-admin worklist
  // runs its own query against the column.
  // CTV-457 Phase C: populated when the publisher rejected this creative via
  // the per-creative reject endpoint. Submission-level reject persists on the
  // submission row instead. Surfaced only on publisher-facing endpoints.
  rejection_reason: string | null;
  rejection_reason_codes: string[] | null;
  // CTV-611: set on a voided row once a recovery add (fix upload, library
  // pick, Waymark rework, or Restore) landed on its submission. Unresolved
  // rejection = status 'voided' AND resolved_at null; drives the detail-page
  // Fix & resubmit CTA and the edit-modal recovery block.
  resolved_at: string | null;
  // CTV-466 Phase 5 / CTV-775: when this creative was rendered from an AI
  // commercial, we surface its id so the advertiser can re-open it in the
  // Waymark editor (via POST /waymark/login-for-creative). Null for raw
  // uploads from disk. Replaces the pre-CTV-775 waymark_commercial_id.
  waymark_commercial_id: string | null;
  created_at: string;
}


export interface SelfServiceMonthlyPaymentRecovery {
  installmentId: string;
  installmentNumber: number;
  amountCents: number;
  serviceFeeCents: number;
  periodStart: string;
  periodEnd: string;
  failedAt: string | null;
  nextAttemptAt: string;
  errorCode: string | null;
  errorMessage: string | null;
}

// CTV-504: per-installment row on monthly submissions. Always empty for
// upfront. Mirrors public.selfservice_payment_installments rows so the
// receipt and any charges-list UI can render the actual current schedule
// (including the bumped tail amount/period after each extension) instead
// of the frozen at-submission split implied by `amount_cents`.
export interface SelfServiceInstallment {
  id: string;
  installmentNumber: number;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  // Total charged for this installment (budget portion + its own service-fee
  // gross-up). Backend cents are authoritative — render verbatim, never
  // recompute.
  amountCents: number;
  serviceFeeCents: number;
  // CTV-518: backend rows may also carry the per-installment budget/tax
  // decomposition. Optional — tolerate absence on older payloads.
  budgetCents?: number;
  taxCents?: number;
  status: 'pending_approval' | 'scheduled' | 'processing' | 'paid' | 'failed' | 'canceled';
  paidAt: string | null;
}

export interface SelfServiceSubmissionRow {
  id: string;
  publisher_slug: string;
  // CTV-759: the station's display name, present ONLY on publisher-facing
  // rows (list-by-publisher). The aggregated Submissions table and merged
  // review queue render it as a per-row tag; every other caller omits it.
  station_name?: string | null;
  contact_email: string;
  business_info: SelfServiceBusinessInfo;
  campaign: SelfServiceCampaign;
  // Creatives are first-class rows now (see ADR CTV-440). `creatives`
  // is the Live Set (status='active'). CTV-611: `pendingCreativeSwap` is
  // gone; per-creative review replaced the edit-level swap review.
  creatives: SelfServiceCreativeRow[];
  // CTV-661: ids of the retired creatives that made up the delivering set
  // immediately BEFORE the open swap: the set "Restore previous commercial"
  // brings back. Derived server-side from the creative_set_update edit payload
  // (deriveRestorablePreSwapCreativeIds); empty/absent when nothing qualifies
  // (first submission, no open swap, etc.). The rows themselves are already on
  // `creatives` (the loader includes `retired`).
  restorablePreSwapCreativeIds?: string[];
  // ADR CTV-732: `publicaReadiness` is gone. Publisher review carries
  // no Publica state at all; /admin/publica-uploads owns that surface.
  monthlyPaymentRecovery?: SelfServiceMonthlyPaymentRecovery | null;
  // CTV-504: live per-installment schedule. Empty array for upfront. The
  // OrderReceipt prefers this over `amount_cents` for monthly so extension
  // bumps are reflected.
  installments: SelfServiceInstallment[];
  // CTV-518 money invariant: amount_cents = total charged to the buyer =
  // budget + tax + service fee. Media spend prefers budget_cents and falls
  // back to amount_cents − service_fee_cents − tax_cents.
  amount_cents: number;
  budget_cents: number | null;
  service_fee_cents: number;
  tax_cents: number;
  // CTV-518: snapshotted rev share, present ONLY on publisher-facing rows
  // (list-by-publisher). Advertiser endpoints intentionally omit it — rev
  // share is not advertiser-visible — so the field is optional, not
  // nullable-required. Publisher share = budget × (1 − rev_share_percent).
  rev_share_percent?: number | null;
  status: SelfServiceSubmissionStatus;
  stripe_payment_intent_id: string | null;
  rejection_reason: string | null;
  rejection_reason_codes: string[] | null;
  reviewed_at: string | null;
  created_at: string;
  acknowledged_at: string | null;
  completion_acknowledged_at: string | null;
  canceled_at: string | null;
  impressions_delivered: number | null;
  pacing_impressions: number | null;
  line_item_cpm: number | null;
  impressions_updated_at: string | null;
  // CTV-462: progress-bar UI reads the Current Flight (delivering, else
  // next-upcoming, else last-completed). For upfront this is the lone flight;
  // for monthly it's the one matching the Current Flight selector. Lifetime
  // metrics (impressions_delivered, pacing_impressions) remain the SUM
  // across all flights. See ADR-0002.
  currentFlight: SelfServiceCurrentFlight | null;
  payment_plan: SelfServicePaymentPlan;
  referral_code: string | null;
  promo_code: string | null;
  // PCI-allowed card fragments populated by the Stripe webhook after the
  // buyer's PaymentIntent reaches `requires_capture`. Null until then.
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

// --- Form Data ---

// CTV-457: per-Creative slot in the wizard's Step 3. The wizard accumulates
// SlotState[] in form data; submit serializes each non-empty slot as a row in
// selfservice_creatives. The Scheduler (rendered when length >= 2) edits the
// dates and weights here. File refs and in-flight upload promises live
// alongside the form in per-slot Maps on the wizard component — they can't be
// serialized into RHF.
export interface CreativeSlotState {
  // Stable client-side identifier. Used to key per-slot Maps (Files, upload
  // promises) on the wizard and to remember which slot a Waymark render
  // belongs to. Never sent to the server.
  slotId: string;
  // Populated by the eager-upload path once Supabase Storage has the bytes.
  // Null between file pick and upload completion.
  storagePath: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  clickThroughUrl: string;
  // CTV-476: per-segment shares. Together they partition [startDate, endDate]
  // with no gaps. Cross-creative Rotation Segments are derived at render time
  // by unioning sibling boundaries; this array is the source of truth for
  // each individual creative's shares.
  segmentShares: Array<{ startDate: string; endDate: string; share: number }>;
  // YYYY-MM-DD strings. Default to the submission window when a slot is
  // first added; the Scheduler clamps within that window.
  startDate: string;
  endDate: string;
  // CTV-466 follow-up: when an advertiser opens a creative_rejected campaign
  // via Fix & Resubmit, voided creatives are carried back into the wizard so
  // the user can see exactly what was rejected. These three fields are
  // populated only on those carried-forward rejection rows; the wizard's
  // commercial step renders them as a red header band + reviewer notes, and
  // hard-gates Next until every rejected slot is either replaced or removed.
  rejected?: boolean;
  rejectionReason?: string | null;
  rejectionReasonCodes?: string[] | null;
  // The original selfservice_creatives.id this slot was hydrated from, so the
  // backend resubmit endpoint can recognize and replace the row in place.
  sourceCreativeId?: string;
  // CTV-775: the AI commercial (selfservice_waymark_commercials.id) this
  // slot's file was rendered from. Persisted with the draft so a refresh
  // keeps the slot committed, and stamped onto the creative row at submit
  // (Waymark deduction + Rework in Waymark). Null / absent for uploads.
  waymarkCommercialId?: string | null;
}

export interface SelfServiceFormData {
  // Step 1: Business Info
  businessName: string;
  businessWebsite: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  // CTV-639: buyer's acceptance of the CTVBuyer Advertiser Terms, gated on
  // step 1. Client-side gate only — not persisted server-side yet.
  acceptAdvertiserTerms: boolean;

  // Step 2: Campaign Setup
  campaignName: string;
  budget: number | null;
  selectedProduct: string;
  startDate: string;
  endDate: string;
  targetMarkets: string[];
  targetingMode?: 'default' | 'custom';
  // CTV-421: custom-targeting tab the user is on. Each tab holds its own
  // selection list — switching tabs does not wipe the others, so the wizard
  // can serialize whichever tab is active on submit.
  // CTV-502: the explicit-ZIPs tab was removed; ZIP targeting now goes
  // through the Radius tool only.
  geoMode?: 'states' | 'dmas' | 'radius';
  selectedStates?: string[];
  selectedDMAs?: string[];
  geoTargetCenter?: { lat: number; lng: number; label: string } | null;
  geoTargetRadiusMiles?: number;
  // Computed once per geocode by the fake-coverage calc in RadiusGeoTargeting.
  // Persisted on the form (not local component state) so leaving and
  // returning to the Radius tab doesn't replay the spinner and clobber
  // the user's slider value. Cleared when the user changes the address.
  geoTargetComputedMinRadius?: number;
  geoTargetZips?: string[];

  // Step 3: Creative
  // Source of truth for the wizard's multi-creative state. Slot 0 lives at
  // index 0; the scheduler reads from this same array.
  creatives: CreativeSlotState[];

  // Step 4: Review
  // CTV-639: terms + refund acceptance moved to the step-1 Advertiser Terms
  // gate; step 5 no longer collects any acceptance booleans.
  paymentPlan: 'upfront' | 'monthly' | null;

  // Referral / promo codes — captured from ?ref= / ?promo= URL params or
  // entered manually on the review step. Stored as the user-typed value;
  // normalized + revalidated server-side at submit.
  referralCode: string;
  promoCode: string;
}

// --- Zod Schemas (per-step) ---

// Accepts "example.com", "sub.example.co.uk", "https://x.io/path", etc.
// Rejects "asdf", "test.", "@@@". Protocol is optional — bare hostnames are
// the common case for business website inputs.
const websitePattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i;

// Matches the format produced by `formatUSPhone` in BusinessInfoStep:
// "(XXX) XXX-XXXX". We don't accept other shapes because the input formats
// digits as the user types — anything that doesn't match this shape is a
// partial / invalid entry.
const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;

export const businessInfoSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  businessWebsite: z
    .string()
    .min(1, 'Business website is required')
    // CTV-734: the website becomes the Publica line item Adomain, which rejects
    // tracking parameters and " ' < > &. Declared BEFORE the regex on purpose:
    // zod runs every check and react-hook-form shows the first issue, so a
    // tagged bare domain (`example.com?utm=x`, which the regex also rejects)
    // reports the tracking message, the one that says what to do.
    .superRefine((value, ctx) => {
      const message = getAdomainUrlError(value);
      if (message) ctx.addIssue({ code: 'custom', message });
    })
    .regex(websitePattern, 'Enter a valid website (e.g. example.com)'),
  contactEmail: z.string().min(1, 'Contact email is required').email('Enter a valid email'),
  contactName: z.string().optional().default(''),
  contactPhone: z
    .string()
    .optional()
    .default('')
    .refine((v) => !v || phonePattern.test(v), 'Enter a valid 10-digit US phone number'),
});

// CTV-639: step-1 acceptance of the CTVBuyer Advertiser Terms. Kept as its own
// schema (not folded into businessInfoSchema) because businessInfoSchema is
// reused standalone as a resolver in BusinessInfoStep tests and by the inline
// advertiser-details save — neither of which should require the terms checkbox.
// buildFullSchema spreads this so the wizard's Continue gate (stepFields[1])
// enforces it. `.literal(true)` fails until the box is checked.
export const advertiserTermsSchema = z.object({
  acceptAdvertiserTerms: z.literal(true, {
    message: 'You must agree to the CTVBuyer Advertiser Terms to continue',
  }),
});

// Base campaign fields (without refinements) — used for building the combined schema.
// Geo fields are declared here so the full wizard schema (buildFullSchema) accepts
// them on the RHF form; superRefine in campaignSetupSchema is what actually
// enforces the cross-field empty-custom rule.
export const campaignSetupBaseSchema = (minBudget: number) =>
  z.object({
    campaignName: z.string().optional().default(''),
    budget: z.number({ message: 'Budget is required' })
      .min(minBudget, `Minimum budget is $${minBudget.toLocaleString()}`),
    selectedProduct: z.string().min(1, 'Select a product'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    // Optional: an empty list means "no geo restriction beyond the publisher's
    // channels" (`targetingMode='default'`). Multi-market publishers may pick
    // a subset; leaving it blank runs across every market the publisher owns.
    // Custom-mode narrowing (states / DMAs / radius) is enforced separately
    // in `applyCampaignCrossFieldRules`.
    targetMarkets: z.array(z.string()).optional().default([]),
    targetingMode: z.enum(['default', 'custom']).optional(),
    geoMode: z.enum(['states', 'dmas', 'radius']).optional(),
    selectedStates: z.array(z.string()).optional(),
    selectedDMAs: z.array(z.string()).optional(),
    geoTargetZips: z.array(z.string()).optional(),
  });

// Shared cross-field refinement applied to BOTH the step-2 standalone schema
// (campaignSetupSchema) AND the wizard's full-form resolver (buildFullSchema in
// SelfServiceWizard.tsx). Living here keeps the two in lockstep so a rule
// added in one place can't drift from the other.
type CampaignCrossFieldData = {
  startDate?: string;
  endDate?: string;
  targetingMode?: 'default' | 'custom';
  selectedStates?: string[];
  selectedDMAs?: string[];
  geoTargetZips?: string[];
};

export function applyCampaignCrossFieldRules(
  data: CampaignCrossFieldData,
  ctx: z.RefinementCtx,
  minDays: number,
): void {
  if (data.startDate && data.endDate) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (!(end > start)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date must be after start date',
      });
    } else {
      const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < minDays) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: `Campaign must run for at least ${minDays} days`,
        });
      }
    }
  }
  if (data.targetingMode === 'custom') {
    const anyGeo =
      (data.selectedStates?.length ?? 0) > 0 ||
      (data.selectedDMAs?.length ?? 0) > 0 ||
      (data.geoTargetZips?.length ?? 0) > 0;
    if (!anyGeo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetingMode'],
        // CTV-689: neutral wording — this one static string serves both
        // national (states-only) and local (radius/states/metros) stations.
        message:
          'Pick at least one location to target, or select the default option above.',
      });
    }
  }
}

// Full campaign schema with cross-field refinements — used for standalone
// step-2 validation testing. The wizard's actual runtime resolver in
// SelfServiceWizard.tsx applies the same `applyCampaignCrossFieldRules` to
// its full-form schema so trigger() and onChange re-validation both see
// every rule.
//
// CTV-489: the per-station max-campaign-days ceiling has been removed
// entirely (column + form input + wizard refinement). The wizard's
// inventory-feasibility check is the effective cap on multi-year runs.
export const campaignSetupSchema = (minBudget: number, minDays: number) => {
  return campaignSetupBaseSchema(minBudget).superRefine((data, ctx) => {
    applyCampaignCrossFieldRules(data, ctx, minDays);
  });
};

// Accepts "example.com" or "www.example.com" by auto-prepending http:// before
// validating; users shouldn't have to type a protocol. We prepend http (not
// https) so that HTTP-only sites still resolve — HTTPS sites auto-redirect
// from http to https anyway.
export const lenientUrl = z
  .string()
  .optional()
  .default('')
  .transform((v) => (v && !/^https?:\/\//i.test(v) ? `http://${v}` : v))
  .refine((v) => v === '' || /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(v), {
    message: 'Enter a valid URL (e.g. www.example.com)',
  });

// Creatives are validated at runtime by the wizard's step-3 advance gate
// (slot 0 has a storagePath, every slot's click-through URL parses cleanly).
// No Zod schema needed for step 3 — the per-slot state lives outside RHF.
export const creativeSchema = z.object({});

export const reviewSchema = z.object({
  paymentPlan: z.enum(['upfront', 'monthly'], { message: 'Choose a payment plan' }),
  referralCode: z.string().optional().default(''),
  promoCode: z.string().optional().default(''),
});

// --- Default Form Values ---

export const defaultFormValues: SelfServiceFormData = {
  businessName: '',
  businessWebsite: '',
  contactEmail: '',
  contactName: '',
  contactPhone: '',
  acceptAdvertiserTerms: false,
  campaignName: '',
  budget: null,
  selectedProduct: '',
  startDate: '',
  endDate: '',
  targetMarkets: [],
  targetingMode: 'default',
  // Matches GeoTargetingTabs' visible default — without this, a buyer who
  // toggles to custom mode and uses the already-showing Radius tab without
  // clicking any tab button submits with geoMode=undefined, and the API
  // serializer silently falls back to the publisher's default markets.
  geoMode: 'radius',
  geoTargetCenter: null,
  geoTargetRadiusMiles: 25,
  geoTargetZips: [],
  creatives: [],
  paymentPlan: null,
  referralCode: '',
  promoCode: '',
};

// --- Helpers ---

export function calculateImpressions(budget: number, cpm: number): number {
  if (!budget || !cpm || cpm <= 0) return 0;
  return Math.floor((budget / cpm) * 1000);
}

// Number of calendar months a campaign window touches.
// Jan 5 → Jan 31 = 1; Jan 5 → Feb 5 = 2; Jan 5 → Mar 31 = 3.
export function calendarMonthsSpanned(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  const s = new Date(startDate.slice(0, 10) + 'T00:00:00');
  const e = new Date(endDate.slice(0, 10) + 'T00:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
}

// CTV-640: pay-upfront bonus tier table in WHOLE PERCENTAGE POINTS (0-100),
// mirroring selfservice-api/src/shared/plays-boost.ts (prepayBoostPercent).
// Every percent in the plays-boost domain is integer points — the code
// columns, /codes/validate, and plays_boost_percent all agree — so the sole
// fraction API below is deprecated.
export function prepayBoostPercent(months: number): number {
  if (!Number.isFinite(months)) return 0;
  if (months >= 4) return 10;
  if (months === 3) return 8;
  if (months === 2) return 5;
  return 0;
}

/**
 * @deprecated Fraction (0-1) variant kept only for the pinned tier table in
 * selfService.test.ts and any straggler callers. Use prepayBoostPercent
 * (integer points) — mixing the two units is the classic silent-0%/1000%
 * boost bug. Kill this once no callers remain.
 */
export function prepayDiscountPercent(months: number): number {
  return prepayBoostPercent(months) / 100;
}

// --- CTV-640 Plays boost ---
//
// A promo code, salesperson referral code, or the pay-upfront bonus grants a
// PLAYS BOOST: a percentage uplift to the impression goal, never a price
// change. These helpers mirror selfservice-api/src/shared/plays-boost.ts
// (resolvePlaysBoost / baseImpressions / bonusImpressions); the shared parity
// vectors live in selfService.test.ts and plays-boost.spec.ts. If either
// side's formula or rounding changes, BOTH must change in the same commit, or
// the wizard previews one play count and the server books another.
//
// Composition rules (settled during CTV-640 grilling, see CONTEXT.md):
//   - Promo vs referral: Math.max — the larger single code wins (promo on
//     ties). They never stack.
//   - Pay-upfront bonus: ADDITIVE with the code winner, on upfront plans
//     spanning 2+ calendar months. Monthly plans get the code boost alone.

export interface PlaysBoostSource {
  key: 'code' | 'prepay';
  label: string;
  /** Integer percentage points, 0-100. NEVER a fraction. */
  percent: number;
}

// Mirror of the backend's sanitizePercent: null/NaN/negative degrade to 0,
// fractions floor (a leaked 0.10 becomes 0%, never 1000%), ceiling-clamped.
function sanitizeBoostPercent(value: number | null | undefined, ceiling: number): number {
  if (value == null || !Number.isFinite(value) || value <= 0) return 0;
  return Math.min(ceiling, Math.floor(value));
}

/**
 * Pill supersede state for the promo/referral inputs. Deliberately
 * asymmetric with selectPlaysBoostSources: the winner label uses >= to pick
 * exactly one winner (promo on ties), while the pills use strict > so a tie
 * leaves BOTH active — each "applies" the same percentage, so neither pill
 * is misleading.
 *
 * `referralPercent: null` = attribution-only referral (or no referral): it
 * has no bonus to lose, so it is never "superseded" and never supersedes.
 */
export function playsBoostWinner(
  promoPercent: number | null | undefined,
  referralPercent: number | null | undefined,
): { promoSupersededByReferral: boolean; referralSupersededByPromo: boolean } {
  const promo = sanitizeBoostPercent(promoPercent, 100);
  const hasReferralBonus = referralPercent != null;
  const ref = sanitizeBoostPercent(referralPercent, 100);
  return {
    promoSupersededByReferral: hasReferralBonus && ref > promo,
    referralSupersededByPromo: hasReferralBonus && promo > ref,
  };
}

export interface PlaysBoostSourceInputs {
  /** Valid promo's discountPercent (integer points), or null/undefined. */
  promoPercent?: number | null;
  promoLabel?: string | null;
  /**
   * Valid referral's defaultDiscountPercent (integer points). null =
   * attribution-only (contributes 0) or no valid referral.
   */
  referralPercent?: number | null;
  referralLabel?: string | null;
  /**
   * Whether this surface counts the pay-upfront bonus. Named argument on
   * purpose: the review page's summary card gates on the SELECTED plan
   * (paymentPlan === 'upfront'), while the plan-comparison cards each pass
   * their own literal (true for the upfront card, false for monthly)
   * regardless of selection — a merchandising affordance, not an accident.
   */
  includePrepayBonus: boolean;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
}

/**
 * Resolve the active boost sources: at most one code source (the Math.max
 * winner, promo on ties) plus the prepay source when included and earned.
 * Mirrors the backend's resolvePlaysBoost composition exactly.
 */
export function selectPlaysBoostSources(inputs: PlaysBoostSourceInputs): PlaysBoostSource[] {
  const promo = sanitizeBoostPercent(inputs.promoPercent, 100);
  const ref = sanitizeBoostPercent(inputs.referralPercent, 100);
  const sources: PlaysBoostSource[] = [];

  const months = calendarMonthsSpanned(inputs.startDate ?? '', inputs.endDate ?? '');
  const prepayPct = inputs.includePrepayBonus && months >= 2 ? prepayBoostPercent(months) : 0;
  if (prepayPct > 0) {
    sources.push({
      key: 'prepay',
      label: `Pay-upfront bonus (${months}-month commit)`,
      percent: prepayPct,
    });
  }

  const codePct = Math.max(promo, ref);
  if (codePct > 0) {
    // Winner label: >= keeps promo on ties, matching the backend's Math.max
    // (which is order-independent on ties because the percents are equal).
    const label = promo >= ref
      ? (inputs.promoLabel || 'Promo code')
      : (inputs.referralLabel || 'Referral code');
    sources.push({ key: 'code', label, percent: codePct });
  }

  return sources;
}

export interface PlaysBoostLine extends PlaysBoostSource {
  /** Display-only slice of the total bonus; the lines sum exactly to bonus. */
  delta: number;
}

export interface PlaysBoostComputation {
  base: number;
  /** Sum of source percents (code winner + prepay), integer points. */
  totalPercent: number;
  /** Combined floor over the total percent — the backend's bonusImpressions. */
  bonus: number;
  total: number;
  lines: PlaysBoostLine[];
  /** budget * 1000 / total when boosted, else the plain CPM. */
  effectiveCpm: number;
}

/**
 * The single place frontend play-count math happens. base and bonus mirror
 * the backend bit for bit (integer base, integer percent, combined floor).
 * The per-line deltas are DISPLAY-ONLY: they allocate the already-computed
 * total bonus across sources with a largest-remainder pass so the itemized
 * rows sum exactly to the displayed total. That allocator is not part of the
 * backend parity contract.
 */
export function computePlaysBoost(
  budget: number | null | undefined,
  cpm: number,
  sources: PlaysBoostSource[],
): PlaysBoostComputation {
  const base = calculateImpressions(budget ?? 0, cpm);
  const active = sources.filter((s) => sanitizeBoostPercent(s.percent, 100) > 0);
  const totalPercent = sanitizeBoostPercent(
    active.reduce((acc, s) => acc + sanitizeBoostPercent(s.percent, 100), 0),
    200,
  );
  const bonus = base > 0 && totalPercent > 0 ? Math.floor((base * totalPercent) / 100) : 0;
  const total = base + bonus;

  // Largest-remainder allocation of the bonus across sources.
  let lines: PlaysBoostLine[] = [];
  if (bonus > 0 && totalPercent > 0) {
    const exact = active.map((s) => (bonus * sanitizeBoostPercent(s.percent, 100)) / totalPercent);
    const floors = exact.map((x) => Math.floor(x));
    let remainder = bonus - floors.reduce((a, b) => a + b, 0);
    const order = exact
      .map((x, i) => ({ i, frac: x - Math.floor(x) }))
      .sort((a, b) => b.frac - a.frac || a.i - b.i);
    for (const { i } of order) {
      if (remainder <= 0) break;
      floors[i] += 1;
      remainder -= 1;
    }
    lines = active.map((s, i) => ({ ...s, delta: floors[i] }));
  }

  const effectiveCpm = bonus > 0 && total > 0 && budget ? (budget * 1000) / total : cpm;
  return { base, totalPercent, bonus, total, lines, effectiveCpm };
}

// Anniversary-day billing schedule. Each period runs from the campaign's
// start day-of-month to the same day next month (e.g. May 10 → Jun 9). The
// BUDGET is split across periods: full anniversary months get the same flat
// monthly portion; the trailing partial month (if any) is prorated against
// what its full period would have been (e.g. Jul 10 – Jul 31 = 22/31 of the
// rate). Day-31 starts clamp to the last day of shorter months.
//
// CTV-635: each installment is its own card charge for exactly that period's
// budget portion, no service-fee gross-up (the buyer pays the budget, and
// Stripe costs come out of the publisher's payout server-side). The entry
// amounts therefore sum exactly to the budget. Used by both the review-step
// UI and the receipt's pre-approval projection, keep them aligned through
// this helper.
export interface ChargeScheduleEntry {
  label: string;
  amount: number;
  coversLabel: string;
  days: number;
  isPartial: boolean;
}

export function buildChargeSchedule(
  startDateStr: string | null | undefined,
  endDateStr: string | null | undefined,
  budget: number,
): ChargeScheduleEntry[] {
  if (!startDateStr || !endDateStr) return [];
  // Coerce to a "YYYY-MM-DD" prefix so callers that accidentally pass a
  // non-string (e.g. a pg Date object) don't produce an Invalid Date and
  // turn the while-loop below into an infinite NaN-comparison loop.
  const startPrefix = String(startDateStr).slice(0, 10);
  const endPrefix = String(endDateStr).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startPrefix) || !/^\d{4}-\d{2}-\d{2}$/.test(endPrefix)) return [];
  const start = parseDateOnlyUtc(startPrefix);
  const end = parseDateOnlyUtc(endPrefix);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  const totalDays = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
  // Bail on backwards dates, NaN/Infinity budgets, and zero/negative budgets.
  // Producing a schedule of negative or $0 charges is misleading; the
  // wizard should not be calling us in those cases anyway.
  if (totalDays <= 0 || !isFinite(budget) || budget <= 0) return [];

  const fmtLong = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const fmtShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

  const anniversary = (n: number): Date => {
    const totalMonths = start.getUTCMonth() + n;
    const year = start.getUTCFullYear() + Math.floor(totalMonths / 12);
    const month = ((totalMonths % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(start.getUTCDate(), lastDayOfTargetMonth)));
  };

  type Raw = { periodStart: Date; periodEnd: Date; naturalDays: number; actualDays: number };
  const raw: Raw[] = [];
  let n = 0;
  // Hard cap at 240 (20 years) so any future date-math regression that
  // produces an Invalid Date can't lock the browser tab.
  while (n < 240) {
    const periodStart = anniversary(n);
    if (isNaN(periodStart.getTime()) || periodStart > end) break;
    const nextAnniv = anniversary(n + 1);
    if (isNaN(nextAnniv.getTime())) break;
    const naturalEnd = new Date(nextAnniv.getTime() - 86400000);
    const periodEnd = naturalEnd > end ? end : naturalEnd;
    const naturalDays = Math.floor((naturalEnd.getTime() - periodStart.getTime()) / 86400000) + 1;
    const actualDays = Math.floor((periodEnd.getTime() - periodStart.getTime()) / 86400000) + 1;
    raw.push({ periodStart, periodEnd, naturalDays, actualDays });
    n += 1;
  }

  const lastIdx = raw.length - 1;
  const lastIsPartial = lastIdx >= 0 && raw[lastIdx].actualDays < raw[lastIdx].naturalDays;
  const fullCount = lastIsPartial ? lastIdx : raw.length;
  const partialFraction = lastIsPartial ? raw[lastIdx].actualDays / raw[lastIdx].naturalDays : 0;
  const denominator = fullCount + partialFraction;
  const budgetCents = Math.round(budget * 100);
  const monthlyRateCents = denominator > 0 ? budgetCents / denominator : 0;

  // Split the BUDGET into per-period portions (penny remainder lands on the
  // last portion so the portions sum exactly to the budget), each portion is
  // its own card charge.
  const budgetPortionsCents: number[] = raw.map((_, i) => {
    const isPartial = lastIsPartial && i === lastIdx;
    return Math.round(isPartial ? monthlyRateCents * partialFraction : monthlyRateCents);
  });
  if (budgetPortionsCents.length > 0) {
    const sumCents = budgetPortionsCents.reduce((s, c) => s + c, 0);
    const diffCents = budgetCents - sumCents;
    if (diffCents !== 0) budgetPortionsCents[budgetPortionsCents.length - 1] += diffCents;
  }

  return raw.map((p, i) => ({
    label: fmtLong(p.periodStart),
    amount: budgetPortionsCents[i] / 100,
    coversLabel: `${fmtShort(p.periodStart)} – ${fmtShort(p.periodEnd)}`,
    days: p.actualDays,
    isPartial: lastIsPartial && i === lastIdx,
  }));
}

// Sum of a charge schedule's per-installment amounts, in dollars, rounded at
// the cent so it equals what the buyer's card is actually charged across all
// installments. This is the authoritative "total over N months" figure —
// under CTV-635 it equals the campaign budget exactly. (CTV-575)
export function chargeScheduleTotal(entries: ChargeScheduleEntry[]): number {
  return entries.reduce((s, e) => s + Math.round(e.amount * 100), 0) / 100;
}

// CTV-504: convert live `selfservice_payment_installments` rows into the
// same ChargeScheduleEntry shape buildChargeSchedule produces. Used by the
// receipt for monthly campaigns so post-extension state (bumped tail
// amount/period, inserted new flights) renders correctly. A partial entry
// is one whose period is shorter than the natural month anchored to its
// start date — same rule the wizard's schedule uses.
export function installmentsToScheduleEntries(
  installments: SelfServiceInstallment[],
): ChargeScheduleEntry[] {
  if (!installments.length) return [];
  const fmtLong = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const fmtShort = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const sorted = [...installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
  return sorted.map((inst) => {
    const start = parseDateOnlyUtc(inst.periodStart);
    const end = parseDateOnlyUtc(inst.periodEnd);
    const validRange = !isNaN(start.getTime()) && !isNaN(end.getTime());
    const days = validRange
      ? Math.floor((end.getTime() - start.getTime()) / 86400000) + 1
      : 0;
    // Natural bucket length is the gap to next anniversary anchored to start
    // day-of-month, with day-31 clamp; mirrors buildChargeSchedule.
    let naturalDays = days;
    if (validRange) {
      const startMonths = start.getUTCMonth() + 1;
      const yr = start.getUTCFullYear() + Math.floor(startMonths / 12);
      const mo = ((startMonths % 12) + 12) % 12;
      const lastOfNext = new Date(Date.UTC(yr, mo + 1, 0)).getUTCDate();
      const nextAnniv = new Date(
        Date.UTC(yr, mo, Math.min(start.getUTCDate(), lastOfNext)),
      );
      naturalDays = Math.floor((nextAnniv.getTime() - start.getTime()) / 86400000);
    }
    return {
      label: validRange ? fmtLong(start) : inst.periodStart,
      amount: inst.amountCents / 100,
      coversLabel: validRange ? `${fmtShort(start)} – ${fmtShort(end)}` : '',
      days,
      isPartial: validRange && days < naturalDays,
    };
  });
}

function parseDateOnlyUtc(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return new Date(NaN);
  }
  return date;
}

// --- Referrals & Promotions ---

// CTV-673: how a salesperson record relates to a station login.
//   linked        — a login owns this record (user_id set).
//   invited       — sign-up invite sent, not yet accepted.
//   email_in_use  — the email can't back a login link (other org,
//                   advertiser account, or claimed by another rep).
//   none          — no email, or nothing pending.
export type SalespersonLinkStatus = 'linked' | 'invited' | 'email_in_use' | 'none';

export interface SelfServiceSalesperson {
  id: string;
  publisher_slug: string;
  name: string;
  email: string | null;
  code: string;
  default_discount_percent: number | null;
  // CTV-671: per-rep commission % override; null = inherit the station default.
  commission_percent: number | null;
  // CTV-673: the linked login and how the record relates to one.
  user_id: string | null;
  link_status?: SalespersonLinkStatus;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// CTV-671: per-rep net revenue + commission aggregation from
// POST /salespeople/commission-report. Revenue is net of CTVBuyer rev share
// and Stripe fees (payout-ledger figures), bucketed by charge month.
export interface CommissionReportMonth {
  month: string; // 'YYYY-MM'
  net_revenue_cents: number;
  commission_cents: number;
  campaign_count: number;
}

// Per-campaign attribution: the submission-time snapshot % (null = campaign
// predates commission config; the current rate applies) and ledger sums so
// far (0 until money is collected).
export interface CommissionReportCampaign {
  submission_id: string;
  // CTV-673: who the campaign is for (advertiser business name); null when
  // the advertiser record is missing.
  advertiser_name: string | null;
  commission_percent: number | null;
  net_revenue_cents: number;
  commission_cents: number;
}

export interface CommissionReportRep {
  salesperson_id: string;
  name: string;
  code: string;
  active: boolean;
  commission_percent: number | null;
  effective_commission_percent: number | null;
  total_net_revenue_cents: number;
  total_commission_cents: number;
  campaign_count: number;
  monthly: CommissionReportMonth[];
  campaigns: CommissionReportCampaign[];
}

export interface CommissionReport {
  default_commission_percent: number | null;
  reps: CommissionReportRep[];
}

// CTV-673: POST /salespeople/my-commission-report — the caller's own slice.
// linked:false = no salesperson record belongs to this login yet.
export interface MyCommissionReport {
  linked: boolean;
  default_commission_percent: number | null;
  rep: CommissionReportRep | null;
}

export interface SelfServicePromotion {
  id: string;
  publisher_slug: string;
  code: string;
  discount_percent: number;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  current_uses: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Default 6-char referral code from a name: first 3 of first name + first 3
// of last name. Pads with unambiguous alphanumerics when the name is short
// (e.g. "Bo Wu" → "BOWU" + 2 random).
export function suggestReferralCode(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = (parts[0] ?? '').replace(/[^A-Za-z]/g, '').toUpperCase();
  const last = (parts.slice(1).join('') ?? '').replace(/[^A-Za-z]/g, '').toUpperCase();
  let head = first.slice(0, 3) + last.slice(0, 3);
  // Drop ambiguous I/O/0/1 from the random pool to avoid "is it a 1 or an I?"
  const pool = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  while (head.length < 6) {
    head += pool[Math.floor(Math.random() * pool.length)];
  }
  return head.slice(0, 6);
}

// Promo codes are publisher-chosen; we only normalize them.
export function normalizePromoCode(s: string): string {
  return s.trim().toUpperCase();
}

// === CTV-776: publisher Billing tab (POST /publisher-billing/summary) ===
// Mirrors selfservice-api/src/modules/publisher-billing/publisher-billing.service.ts.
// All money fields are integer cents.

export interface PublisherBillingSalesRep {
  salesperson_id: string;
  name: string;
  /** Frozen at redemption (CTV-673); the marker only exists when this is set. */
  commission_percent: number | null;
}

export interface PublisherBillingMonth {
  month: string;
  period_start: string;
  period_end: string;
  is_final: boolean;
  delivered_impressions: number;
  invoiced_impressions: number;
  bonus_impressions: number;
  /** Counted for exempt orgs too; the exemption zeroes the deduction only. */
  waymark_impressions: number;
  /** Gross revenue (CTV-780): the advertiser's money recognized this month, before the rev share. */
  gross_revenue_cents: number;
  /** Net revenue (CTV-780; was "Your share"): the publisher's share of gross revenue recognized this month. */
  net_revenue_cents: number;
  /** Charged deductions: what the schedule subtracts. */
  waymark_deduction_cents: number;
  ad_serving_deduction_cents: number;
  stripe_fee_cents: number;
  /** Retained deductions: what actually came out of the share this month. */
  waymark_retained_cents: number;
  ad_serving_retained_cents: number;
  stripe_fee_retained_cents: number;
  /** Payout: what the station receives for this month, floored at zero. */
  net_payout_cents: number;
  /** True when the floor bound: deductions exceeded net revenue this month. */
  payout_floored: boolean;
  /** Row-level state; the PAGE shows the station-month batch status (CTV-780). */
  display_status: 'paid' | 'due_now' | 'expected';
  payable_at: string;
  settled_at: string | null;
  stripe_transfer_id: string | null;
  /** Frozen percent x net revenue; 0 without a rep. */
  commission_cents: number;
}

/** The station-month payout batch (CTV-780 / ADR-0044): one payment status per delivery month. */
export interface PublisherBillingStationMonth {
  month: string;
  net_payout_cents: number;
  /** due_now beats expected beats paid; nothing_due when every row nets to zero. */
  display_status: 'paid' | 'due_now' | 'expected' | 'nothing_due';
  payable_at: string;
  settled_at: string | null;
  /** The shared transfer id when every paid row carries the same one. */
  stripe_transfer_id: string | null;
}

export interface PublisherBillingCampaign {
  submission_id: string;
  campaign_name: string;
  advertiser_name: string | null;
  status: string;
  payment_plan: string;
  start_date: string;
  end_date: string;
  gross_cpm_dollars: number | null;
  gross_cpm_uniform: boolean;
  /** eCPM (CTV-780): budget / (invoiced base + bonus) x 1000; null without a goal. */
  effective_cpm_dollars: number | null;
  rev_share_percent: number;
  waymark_exempt: boolean;
  /** The org's passthrough rates (dollars per 1,000), for the fee hovers (CTV-780). */
  waymark_cpm: number;
  ad_serving_cpm: number;
  plays_boost_percent: number;
  promo_code: string | null;
  referral_code: string | null;
  sales_rep: PublisherBillingSalesRep | null;
  payout_paid_cents: number;
  still_owed_cents: number;
  months: PublisherBillingMonth[];
}

export interface PublisherBillingStation {
  station_id: string;
  slug: string | null;
  name: string;
  display_name: string | null;
  still_owed_cents: number;
  next_payable_at: string | null;
  payout_destination_ready: boolean;
  campaigns: PublisherBillingCampaign[];
  /** The station-month payout batches (CTV-780), newest first. */
  months: PublisherBillingStationMonth[];
}

export interface PublisherBillingSummary {
  month: string | null;
  today: string;
  stations: PublisherBillingStation[];
}
